import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { useUIStore } from '../../stores/ui-store';
import {
  TemplateFormData,
  TemplateScope,
  TemplateStructure,
  DifficultyLevel,
  getTemplateScopeName,
  getTemplateStructureName,
  getDifficultyLevelName,
  PlatformTemplateDetailResponse,
  PlatformTemplateSummaryResponse,
  TemplateBlockFormData,
  TemplateExerciseFormData,
  BlockOption,
  ExerciseOption
} from './types';
import {
  useCreatePlatformTemplate,
  useUpdatePlatformTemplate,
  usePlatformBlockOptions,
  usePlatformExerciseOptions,
  usePlatformEquipmentOptions,
  usePlatformTemplateDetail
} from './api';

interface TemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  template?: PlatformTemplateDetailResponse | PlatformTemplateSummaryResponse;
  mode: 'create' | 'edit';
}

const initialFormData: TemplateFormData = {
  name: '',
  description: '',
  durationMinutes: 60,
  primaryEquipmentId: undefined,
  difficulty: DifficultyLevel.Beginner,
  scope: TemplateScope.Global,
  structure: TemplateStructure.FlowSequence,
  maxParticipants: undefined,
  tags: '',
  originalAuthor: '',
  isActive: true,
  blocks: [],
  exercises: [],
};

const initialBlockData: TemplateBlockFormData = {
  blockId: 0,
  blockName: '',
  orderIndex: 1,
  isOptional: false,
  overrideDurationMinutes: undefined,
  notes: '',
  transitionSeconds: 30,
};

const initialExerciseData: TemplateExerciseFormData = {
  exerciseId: 0,
  exerciseName: '',
  orderIndex: 1,
  durationSeconds: undefined,
  repetitions: undefined,
  springSettings: '',
  equipmentSetupId: undefined,
  notes: '',
  transitionSeconds: 15,
  isOptional: false,
};

export function TemplateForm({ isOpen, onClose, template, mode }: TemplateFormProps) {
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'blocks' | 'exercises'>('blocks');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingExerciseIndex, setEditingExerciseIndex] = useState<number | null>(null);
  const [currentBlock, setCurrentBlock] = useState<TemplateBlockFormData>(initialBlockData);
  const [currentExercise, setCurrentExercise] = useState<TemplateExerciseFormData>(initialExerciseData);
  
  const { addNotification } = useUIStore();
  const createMutation = useCreatePlatformTemplate();
  const updateMutation = useUpdatePlatformTemplate();
  const { data: blockData } = usePlatformBlockOptions();
  const { data: exerciseData } = usePlatformExerciseOptions();
  const { data: equipmentData } = usePlatformEquipmentOptions();
  
  // Fetch detailed template data when editing
  const { data: detailedTemplate, isLoading: isLoadingDetail } = usePlatformTemplateDetail(
    mode === 'edit' && template ? template.id : 0
  );

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'create') {
        setFormData(initialFormData);
      } else if (mode === 'edit' && template) {
        // For edit mode, prioritize detailed data but only populate if we have it or aren't loading
        if (detailedTemplate) {
          // We have detailed data with blocks and exercises
          setFormData({
            name: detailedTemplate.name,
            description: detailedTemplate.description || '',
            durationMinutes: detailedTemplate.durationMinutes,
            primaryEquipmentId: detailedTemplate.primaryEquipmentId,
            difficulty: detailedTemplate.difficulty,
            scope: detailedTemplate.scope,
            structure: detailedTemplate.structure,
            maxParticipants: detailedTemplate.maxParticipants,
            tags: detailedTemplate.tags || '',
            originalAuthor: detailedTemplate.originalAuthor || '',
            isActive: detailedTemplate.isActive,
            blocks: detailedTemplate.blocks.map(block => ({
              blockId: block.blockId,
              blockName: block.blockName,
              orderIndex: block.orderIndex,
              isOptional: block.isOptional,
              overrideDurationMinutes: block.overrideDurationMinutes,
              notes: block.notes || '',
              transitionSeconds: block.transitionSeconds,
            })),
            exercises: detailedTemplate.exercises.map(exercise => ({
              exerciseId: exercise.exerciseId,
              exerciseName: exercise.exerciseName,
              orderIndex: exercise.orderIndex,
              durationSeconds: exercise.durationSeconds,
              repetitions: exercise.repetitions,
              springSettings: exercise.springSettings || '',
              equipmentSetupId: exercise.equipmentSetupId,
              notes: exercise.notes || '',
              transitionSeconds: exercise.transitionSeconds,
              isOptional: exercise.isOptional,
            })),
          });
        } else if (!isLoadingDetail) {
          // We're not loading detailed data, so fall back to summary (no blocks/exercises)
          setFormData({
            name: template.name,
            description: template.description || '',
            durationMinutes: template.durationMinutes,
            primaryEquipmentId: 'primaryEquipmentId' in template ? template.primaryEquipmentId : undefined,
            difficulty: template.difficulty,
            scope: 'scope' in template ? template.scope : TemplateScope.Global,
            structure: 'structure' in template ? template.structure : TemplateStructure.FlowSequence,
            maxParticipants: 'maxParticipants' in template ? template.maxParticipants : undefined,
            tags: template.tags || '',
            originalAuthor: 'originalAuthor' in template ? template.originalAuthor || '' : '',
            isActive: template.isActive,
            blocks: [],
            exercises: [],
          });
        }
        // If isLoadingDetail is true, don't populate yet - wait for detailed data
      }
      setErrors({});
      setActiveTab('blocks');
    }
  }, [isOpen, mode, template, detailedTemplate, isLoadingDetail]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (formData.durationMinutes < 1) {
      newErrors.durationMinutes = 'Duration must be at least 1 minute';
    }

    if (formData.maxParticipants && formData.maxParticipants < 1) {
      newErrors.maxParticipants = 'Max participants must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const blockRequests = formData.blocks.map(block => ({
        blockId: block.blockId,
        orderIndex: block.orderIndex,
        isOptional: block.isOptional,
        overrideDurationMinutes: block.overrideDurationMinutes || undefined,
        notes: block.notes.trim() || undefined,
        transitionSeconds: block.transitionSeconds,
      }));

      const exerciseRequests = formData.exercises.map(exercise => ({
        exerciseId: exercise.exerciseId,
        orderIndex: exercise.orderIndex,
        durationSeconds: exercise.durationSeconds || undefined,
        repetitions: exercise.repetitions || undefined,
        springSettings: exercise.springSettings.trim() || undefined,
        equipmentSetupId: exercise.equipmentSetupId || undefined,
        notes: exercise.notes.trim() || undefined,
        transitionSeconds: exercise.transitionSeconds,
        isOptional: exercise.isOptional,
      }));

      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          durationMinutes: formData.durationMinutes,
          primaryEquipmentId: formData.primaryEquipmentId || undefined,
          difficulty: formData.difficulty,
          scope: formData.scope,
          structure: formData.structure,
          maxParticipants: formData.maxParticipants || undefined,
          tags: formData.tags.trim() || undefined,
          originalAuthor: formData.originalAuthor.trim() || undefined,
          blocks: blockRequests.length > 0 ? blockRequests : undefined,
          exercises: exerciseRequests.length > 0 ? exerciseRequests : undefined,
        });

        addNotification({
          type: 'success',
          title: 'Template Created',
          message: `${formData.name} has been created successfully.`,
        });
      } else {
        if (!template) return;

        await updateMutation.mutateAsync({
          id: template.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          durationMinutes: formData.durationMinutes,
          primaryEquipmentId: formData.primaryEquipmentId || undefined,
          difficulty: formData.difficulty,
          scope: formData.scope,
          structure: formData.structure,
          maxParticipants: formData.maxParticipants || undefined,
          tags: formData.tags.trim() || undefined,
          originalAuthor: formData.originalAuthor.trim() || undefined,
          isActive: formData.isActive ?? true,
          blocks: blockRequests.length > 0 ? blockRequests : undefined,
          exercises: exerciseRequests.length > 0 ? exerciseRequests : undefined,
        });

        addNotification({
          type: 'success',
          title: 'Template Updated',
          message: `${formData.name} has been updated successfully.`,
        });
      }

      onClose();
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: `Failed to ${mode === 'create' ? 'Create' : 'Update'} Template`,
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleInputChange = (field: keyof TemplateFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Block management functions
  const handleAddBlock = () => {
    setCurrentBlock({ ...initialBlockData, orderIndex: formData.blocks.length + 1 });
    setEditingBlockIndex(null);
    setShowBlockModal(true);
  };

  const handleEditBlock = (index: number) => {
    setCurrentBlock({ ...formData.blocks[index] });
    setEditingBlockIndex(index);
    setShowBlockModal(true);
  };

  const handleSaveBlock = () => {
    const blocks = [...formData.blocks];
    
    if (editingBlockIndex !== null) {
      // Edit existing block
      blocks[editingBlockIndex] = { ...currentBlock };
    } else {
      // Add new block
      blocks.push({ ...currentBlock });
    }
    
    // Re-index blocks
    blocks.forEach((block, index) => {
      block.orderIndex = index + 1;
    });
    
    setFormData(prev => ({ ...prev, blocks }));
    setShowBlockModal(false);
  };

  const handleRemoveBlock = (index: number) => {
    const blocks = formData.blocks.filter((_, i) => i !== index);
    // Re-index blocks
    blocks.forEach((block, idx) => {
      block.orderIndex = idx + 1;
    });
    setFormData(prev => ({ ...prev, blocks }));
  };

  const handleMoveBlock = (index: number, direction: 'up' | 'down') => {
    const blocks = [...formData.blocks];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < blocks.length) {
      [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];
      // Re-index blocks
      blocks.forEach((block, idx) => {
        block.orderIndex = idx + 1;
      });
      setFormData(prev => ({ ...prev, blocks }));
    }
  };

  // Exercise management functions
  const handleAddExercise = () => {
    setCurrentExercise({ ...initialExerciseData, orderIndex: formData.exercises.length + 1 });
    setEditingExerciseIndex(null);
    setShowExerciseModal(true);
  };

  const handleEditExercise = (index: number) => {
    setCurrentExercise({ ...formData.exercises[index] });
    setEditingExerciseIndex(index);
    setShowExerciseModal(true);
  };

  const handleSaveExercise = () => {
    const exercises = [...formData.exercises];
    
    if (editingExerciseIndex !== null) {
      // Edit existing exercise
      exercises[editingExerciseIndex] = { ...currentExercise };
    } else {
      // Add new exercise
      exercises.push({ ...currentExercise });
    }
    
    // Re-index exercises
    exercises.forEach((exercise, index) => {
      exercise.orderIndex = index + 1;
    });
    
    setFormData(prev => ({ ...prev, exercises }));
    setShowExerciseModal(false);
  };

  const handleRemoveExercise = (index: number) => {
    const exercises = formData.exercises.filter((_, i) => i !== index);
    // Re-index exercises
    exercises.forEach((exercise, idx) => {
      exercise.orderIndex = idx + 1;
    });
    setFormData(prev => ({ ...prev, exercises }));
  };

  const handleMoveExercise = (index: number, direction: 'up' | 'down') => {
    const exercises = [...formData.exercises];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < exercises.length) {
      [exercises[index], exercises[newIndex]] = [exercises[newIndex], exercises[index]];
      // Re-index exercises
      exercises.forEach((exercise, idx) => {
        exercise.orderIndex = idx + 1;
      });
      setFormData(prev => ({ ...prev, exercises }));
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const blockOptions = blockData?.blocks || [];
  const exerciseOptions = exerciseData?.exercises || [];
  const equipmentOptions = equipmentData?.equipment || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-7xl max-h-[90vh] overflow-hidden">
        <CardHeader>
          <CardTitle>
            {mode === 'create' ? 'Create New Template' : 'Edit Template'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {mode === 'edit' && isLoadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 mx-auto mb-4">
                  <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="text-gray-600">Loading template details...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex h-[80vh]">
              {/* Left Panel - Basic Fields (30%) */}
              <div className="w-[30%] p-6 border-r border-gray-200 overflow-y-auto">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Details</h3>

                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                      placeholder="Enter template name"
                      disabled={isLoading}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Duration */}
                  <div>
                    <label htmlFor="durationMinutes" className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes) *
                    </label>
                    <Input
                      id="durationMinutes"
                      type="number"
                      min="1"
                      value={formData.durationMinutes}
                      onChange={(e) => handleInputChange('durationMinutes', parseInt(e.target.value) || 1)}
                      className={errors.durationMinutes ? 'border-red-500' : ''}
                      disabled={isLoading}
                    />
                    {errors.durationMinutes && (
                      <p className="text-red-500 text-xs mt-1">{errors.durationMinutes}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Enter template description"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty *
                    </label>
                    <select
                      id="difficulty"
                      value={formData.difficulty}
                      onChange={(e) => handleInputChange('difficulty', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {Object.values(DifficultyLevel)
                        .filter(value => typeof value === 'number')
                        .map((difficulty) => (
                          <option key={difficulty} value={difficulty}>
                            {getDifficultyLevelName(difficulty as DifficultyLevel)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Scope */}
                  <div>
                    <label htmlFor="scope" className="block text-sm font-medium text-gray-700 mb-1">
                      Scope *
                    </label>
                    <select
                      id="scope"
                      value={formData.scope}
                      onChange={(e) => handleInputChange('scope', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {Object.values(TemplateScope)
                        .filter(value => typeof value === 'number')
                        .map((scope) => (
                          <option key={scope} value={scope}>
                            {getTemplateScopeName(scope as TemplateScope)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Structure */}
                  <div>
                    <label htmlFor="structure" className="block text-sm font-medium text-gray-700 mb-1">
                      Structure *
                    </label>
                    <select
                      id="structure"
                      value={formData.structure}
                      onChange={(e) => handleInputChange('structure', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      {Object.values(TemplateStructure)
                        .filter(value => typeof value === 'number')
                        .map((structure) => (
                          <option key={structure} value={structure}>
                            {getTemplateStructureName(structure as TemplateStructure)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Primary Equipment */}
                  <div>
                    <label htmlFor="primaryEquipmentId" className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Equipment
                    </label>
                    <select
                      id="primaryEquipmentId"
                      value={formData.primaryEquipmentId || ''}
                      onChange={(e) => handleInputChange('primaryEquipmentId', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      <option value="">No specific equipment</option>
                      {equipmentOptions.map((equipment) => (
                        <option key={equipment.id} value={equipment.id}>
                          {equipment.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Max Participants */}
                  <div>
                    <label htmlFor="maxParticipants" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Participants
                    </label>
                    <Input
                      id="maxParticipants"
                      type="number"
                      min="1"
                      value={formData.maxParticipants || ''}
                      onChange={(e) => handleInputChange('maxParticipants', e.target.value ? parseInt(e.target.value) : undefined)}
                      className={errors.maxParticipants ? 'border-red-500' : ''}
                      placeholder="No limit"
                      disabled={isLoading}
                    />
                    {errors.maxParticipants && (
                      <p className="text-red-500 text-xs mt-1">{errors.maxParticipants}</p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <Input
                      id="tags"
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleInputChange('tags', e.target.value)}
                      placeholder="e.g., beginner, full-body, strength"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Original Author */}
                  <div>
                    <label htmlFor="originalAuthor" className="block text-sm font-medium text-gray-700 mb-1">
                      Original Author
                    </label>
                    <Input
                      id="originalAuthor"
                      type="text"
                      value={formData.originalAuthor}
                      onChange={(e) => handleInputChange('originalAuthor', e.target.value)}
                      placeholder="Enter author name"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Is Active (only for edit mode) */}
                  {mode === 'edit' && (
                    <div className="flex items-center space-x-2">
                      <input
                        id="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                      <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                        Active
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel - Blocks and Exercises (70%) */}
              <div className="flex-1 flex flex-col">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 p-6 pb-0">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      type="button"
                      onClick={() => setActiveTab('blocks')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'blocks'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Session Blocks ({formData.blocks.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('exercises')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'exercises'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Individual Exercises ({formData.exercises.length})
                    </button>
                  </nav>
                </div>

                {/* Tab Content Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    {activeTab === 'blocks' ? (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900">Session Blocks</h3>
                        <Button
                          type="button"
                          onClick={handleAddBlock}
                          variant="outline"
                          disabled={isLoading}
                        >
                          Add Block
                        </Button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900">Individual Exercises</h3>
                        <Button
                          type="button"
                          onClick={handleAddExercise}
                          variant="outline"
                          disabled={isLoading}
                        >
                          Add Exercise
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 p-6 overflow-y-auto">
                  {activeTab === 'blocks' && (
                    <div>
                      {formData.blocks.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <p className="text-lg mb-2">No blocks added yet</p>
                          <p className="text-sm">Click "Add Block" above to start building your template</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {formData.blocks.map((block, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {block.orderIndex}. {block.blockName || `Block ${block.blockId}`}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {block.overrideDurationMinutes && `${block.overrideDurationMinutes} min`}
                                  {block.isOptional && ` • Optional`}
                                  {block.notes && ` • ${block.notes}`}
                                </div>
                              </div>
                              <div className="flex space-x-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMoveBlock(index, 'up')}
                                  disabled={index === 0 || isLoading}
                                >
                                  ↑
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMoveBlock(index, 'down')}
                                  disabled={index === formData.blocks.length - 1 || isLoading}
                                >
                                  ↓
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditBlock(index)}
                                  disabled={isLoading}
                                >
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRemoveBlock(index)}
                                  disabled={isLoading}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'exercises' && (
                    <div>
                      {formData.exercises.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <p className="text-lg mb-2">No exercises added yet</p>
                          <p className="text-sm">Click "Add Exercise" above to start building your template</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {formData.exercises.map((exercise, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {exercise.orderIndex}. {exercise.exerciseName || `Exercise ${exercise.exerciseId}`}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {exercise.durationSeconds && `${exercise.durationSeconds}s`}
                                  {exercise.repetitions && ` • ${exercise.repetitions} reps`}
                                  {exercise.springSettings && ` • ${exercise.springSettings}`}
                                  {exercise.isOptional && ` • Optional`}
                                </div>
                              </div>
                              <div className="flex space-x-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMoveExercise(index, 'up')}
                                  disabled={index === 0 || isLoading}
                                >
                                  ↑
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMoveExercise(index, 'down')}
                                  disabled={index === formData.exercises.length - 1 || isLoading}
                                >
                                  ↓
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditExercise(index)}
                                  disabled={isLoading}
                                >
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRemoveExercise(index)}
                                  disabled={isLoading}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Form Actions - Fixed at bottom of right panel */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="min-w-[80px]"
                    >
                      {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Block Modal */}
      {showBlockModal && (
        <BlockModal
          block={currentBlock}
          blocks={blockOptions}
          onSave={handleSaveBlock}
          onCancel={() => setShowBlockModal(false)}
          onChange={(field, value) => setCurrentBlock(prev => ({ ...prev, [field]: value }))}
        />
      )}

      {/* Exercise Modal */}
      {showExerciseModal && (
        <ExerciseModal
          exercise={currentExercise}
          exercises={exerciseOptions}
          onSave={handleSaveExercise}
          onCancel={() => setShowExerciseModal(false)}
          onChange={(field, value) => setCurrentExercise(prev => ({ ...prev, [field]: value }))}
        />
      )}
    </div>
  );
}

// Block selection and configuration modal
interface BlockModalProps {
  block: TemplateBlockFormData;
  blocks: BlockOption[];
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: keyof TemplateBlockFormData, value: any) => void;
}

function BlockModal({ block, blocks, onSave, onCancel, onChange }: BlockModalProps) {
  const selectedBlockOption = blocks.find(b => b.id === block.blockId);

  const handleBlockSelect = (blockId: number) => {
    const selected = blocks.find(b => b.id === blockId);
    onChange('blockId', blockId);
    onChange('blockName', selected?.name || '');
    // Auto-fill duration if available
    if (selected?.durationMinutes) {
      onChange('overrideDurationMinutes', selected.durationMinutes);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Configure Block</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Block Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Block *
            </label>
            <select
              value={block.blockId || ''}
              onChange={(e) => handleBlockSelect(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a block...</option>
              {blocks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.category} - {b.durationMinutes} min)
                </option>
              ))}
            </select>
          </div>

          {selectedBlockOption && (
            <div className="p-3 bg-blue-50 rounded-md text-sm">
              <p><strong>Category:</strong> {selectedBlockOption.category}</p>
              <p><strong>Difficulty:</strong> {selectedBlockOption.difficulty}</p>
              <p><strong>Exercises:</strong> {selectedBlockOption.exerciseCount}</p>
            </div>
          )}

          {/* Override Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Override Duration (minutes)
            </label>
            <Input
              type="number"
              min="1"
              value={block.overrideDurationMinutes || ''}
              onChange={(e) => onChange('overrideDurationMinutes', parseInt(e.target.value) || undefined)}
              placeholder="Use block default"
            />
          </div>

          {/* Transition Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transition Time (seconds)
            </label>
            <Input
              type="number"
              min="0"
              value={block.transitionSeconds}
              onChange={(e) => onChange('transitionSeconds', parseInt(e.target.value) || 30)}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={block.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Special instructions or modifications"
            />
          </div>

          {/* Is Optional */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={block.isOptional}
              onChange={(e) => onChange('isOptional', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label className="text-sm font-medium text-gray-700">
              Optional block
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={!block.blockId}>
              Save Block
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Exercise selection and configuration modal
interface ExerciseModalProps {
  exercise: TemplateExerciseFormData;
  exercises: ExerciseOption[];
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: keyof TemplateExerciseFormData, value: any) => void;
}

function ExerciseModal({ exercise, exercises, onSave, onCancel, onChange }: ExerciseModalProps) {
  const selectedExerciseOption = exercises.find(ex => ex.id === exercise.exerciseId);

  const handleExerciseSelect = (exerciseId: number) => {
    const selected = exercises.find(ex => ex.id === exerciseId);
    onChange('exerciseId', exerciseId);
    onChange('exerciseName', selected?.name || '');
    // Auto-fill defaults if available
    if (selected?.typicalDurationSeconds) {
      onChange('durationSeconds', selected.typicalDurationSeconds);
    }
    if (selected?.defaultReps) {
      onChange('repetitions', selected.defaultReps);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Configure Exercise</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Exercise Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Exercise *
            </label>
            <select
              value={exercise.exerciseId || ''}
              onChange={(e) => handleExerciseSelect(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an exercise...</option>
              {exercises.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name} ({ex.category})
                </option>
              ))}
            </select>
          </div>

          {selectedExerciseOption && (
            <div className="p-3 bg-blue-50 rounded-md text-sm">
              <p><strong>Equipment:</strong> {selectedExerciseOption.primaryEquipmentName}</p>
              <p><strong>Difficulty:</strong> {selectedExerciseOption.difficulty}</p>
            </div>
          )}

          {/* Duration and Reps */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (seconds)
              </label>
              <Input
                type="number"
                min="1"
                value={exercise.durationSeconds || ''}
                onChange={(e) => onChange('durationSeconds', parseInt(e.target.value) || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repetitions
              </label>
              <Input
                type="number"
                min="1"
                value={exercise.repetitions || ''}
                onChange={(e) => onChange('repetitions', parseInt(e.target.value) || undefined)}
              />
            </div>
          </div>

          {/* Spring Settings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spring Settings
            </label>
            <Input
              type="text"
              value={exercise.springSettings}
              onChange={(e) => onChange('springSettings', e.target.value)}
              placeholder="e.g., 2 Red, 1 Blue"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={exercise.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Special instructions or modifications"
            />
          </div>

          {/* Transition Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transition Time (seconds)
            </label>
            <Input
              type="number"
              min="0"
              value={exercise.transitionSeconds}
              onChange={(e) => onChange('transitionSeconds', parseInt(e.target.value) || 15)}
            />
          </div>

          {/* Is Optional */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={exercise.isOptional}
              onChange={(e) => onChange('isOptional', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label className="text-sm font-medium text-gray-700">
              Optional exercise
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={!exercise.exerciseId}>
              Save Exercise
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}